import Api from "../services/AxiosService";
import { unwrap } from "./envelope";

export type SherexMaterial =
  | "aluminum"
  | "steel"
  | "high_strength_steel"
  | "stainless_steel"
  | "copper"
  | "nickel_copper_alloy"
  | "monel";

export type SherexEndType = "blind" | "open";

export interface SherexTools {
  flex5: boolean;
  flex18: boolean;
  riv943: boolean;
  riv949: boolean;
  ssg800Au: boolean;
  ssg800Av: boolean;
}

export interface SherexFastener {
  partNumber: string;
  rivetNutType: string | null;
  series: string | null;
  material: SherexMaterial | null;
  endType: SherexEndType | null;
  diameterUs: string | null;
  diameterMetric: string | null;
  minHoleSizeUs: string | null;
  minHoleSizeMetric: string | null;
  maxHoleSizeUs: string | null;
  maxHoleSizeMetric: string | null;
  minGripRangeUs: string | null;
  minGripRangeMetric: string | null;
  maxGripRangeUs: string | null;
  maxGripRangeMetric: string | null;
  bodyLengthUs: string | null;
  bodyLengthMetric: string | null;
  fullLengthUs: string | null;
  fullLengthMetric: string | null;
  flangeDiameterUs: string | null;
  flangeDiameterMetric: string | null;
  flangeThicknessUs: string | null;
  flangeThicknessMetric: string | null;
  installedLengthUs: string | null;
  installedLengthMetric: string | null;
  shearStrengthUsLbf: string | null;
  shearStrengthMetricKn: string | null;
  tensileStrengthUsLbf: string | null;
  tensileStrengthMetricKn: string | null;
  radialDeflectionInch: string | null;
  radialDeflectionMm: string | null;
  tools: SherexTools;
  minRecommendedInstallForceLbs: string | null;
  maxRecommendedInstallForceLbs: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PaginatedSherexFasteners {
  data: SherexFastener[];
  pagination: PaginationMeta;
}

export interface ListSherexFastenersParams {
  page?: number;
  limit?: number;
  search?: string;
}

export interface SherexFastenerOptions {
  rivetNutTypes: string[];
  series: string[];
  materials: SherexMaterial[];
  endTypes: SherexEndType[];
  tools: (keyof SherexTools)[];
}

type DimensionFields = Partial<{
  diameterUs: string | number | null;
  diameterMetric: string | number | null;
  minHoleSizeUs: string | number | null;
  minHoleSizeMetric: string | number | null;
  maxHoleSizeUs: string | number | null;
  maxHoleSizeMetric: string | number | null;
  minGripRangeUs: string | number | null;
  minGripRangeMetric: string | number | null;
  maxGripRangeUs: string | number | null;
  maxGripRangeMetric: string | number | null;
  bodyLengthUs: string | number | null;
  bodyLengthMetric: string | number | null;
  fullLengthUs: string | number | null;
  fullLengthMetric: string | number | null;
  flangeDiameterUs: string | number | null;
  flangeDiameterMetric: string | number | null;
  flangeThicknessUs: string | number | null;
  flangeThicknessMetric: string | number | null;
  installedLengthUs: string | number | null;
  installedLengthMetric: string | number | null;
  shearStrengthUsLbf: string | number | null;
  shearStrengthMetricKn: string | number | null;
  tensileStrengthUsLbf: string | number | null;
  tensileStrengthMetricKn: string | number | null;
  radialDeflectionInch: string | number | null;
  radialDeflectionMm: string | number | null;
}>;

export interface CreateSherexFastenerBody extends DimensionFields {
  partNumber: string;
  rivetNutType?: string | null;
  series?: string | null;
  material?: SherexMaterial | null;
  endType?: SherexEndType | null;
  tools?: Partial<SherexTools>;
  minRecommendedInstallForceLbs?: string | number | null;
  maxRecommendedInstallForceLbs?: string | number | null;
}

export interface UpdateSherexFastenerBody extends DimensionFields {
  rivetNutType?: string | null;
  series?: string | null;
  material?: SherexMaterial | null;
  endType?: SherexEndType | null;
  tools?: Partial<SherexTools>;
  minRecommendedInstallForceLbs?: string | number | null;
  maxRecommendedInstallForceLbs?: string | number | null;
}

export async function listSherexFasteners(
  params: ListSherexFastenersParams = {},
): Promise<PaginatedSherexFasteners> {
  const res = await Api.get("/v1/sherex-fasteners", { params });
  return unwrap<PaginatedSherexFasteners>(res.data);
}

export async function getSherexFastener(partNumber: string): Promise<SherexFastener> {
  const res = await Api.get(`/v1/sherex-fasteners/${encodeURIComponent(partNumber)}`);
  return unwrap<SherexFastener>(res.data);
}

export async function listSherexFastenerOptions(): Promise<SherexFastenerOptions> {
  const res = await Api.get("/v1/sherex-fasteners/options");
  return unwrap<SherexFastenerOptions>(res.data);
}

export async function createSherexFastener(
  body: CreateSherexFastenerBody,
): Promise<SherexFastener> {
  const res = await Api.post("/v1/sherex-fasteners", body);
  return unwrap<SherexFastener>(res.data);
}

export async function updateSherexFastener(
  partNumber: string,
  body: UpdateSherexFastenerBody,
): Promise<SherexFastener> {
  const res = await Api.patch(
    `/v1/sherex-fasteners/${encodeURIComponent(partNumber)}`,
    body,
  );
  return unwrap<SherexFastener>(res.data);
}

export async function deleteSherexFastener(
  partNumber: string,
): Promise<{ success: true }> {
  const res = await Api.delete(`/v1/sherex-fasteners/${encodeURIComponent(partNumber)}`);
  return unwrap<{ success: true }>(res.data);
}
